import { type BlokProps, StoryblokComponent } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import type { PropsWithChildren } from "react";
import type { StoryblokStory } from "storyblok-generate-ts";
import { BlokWrap } from "~/components/bloks/BlokWrap";
import type {
	ArticleCardStoryblok,
	ArticleStoryblok,
	MediaCardStoryblok,
} from "~/generated/sb/blok-types";
import { Link } from "~/i18n/routing";
import { sizes as getSizes } from "~/lib/utils/sizes";
import type { BlokData } from "~/srpc/routes/story.router";
import styles from "./ArticleCardBlok.module.css";

const ArticleCard = ({
	imageBlok,
	sizes = getSizes(100, 50),
	heading,
	fullslug,
	description,
	linkOnImage,
	tag,
	slideshow,
	preview,
}: {
	imageBlok: MediaCardStoryblok | undefined;
	sizes: string;
	heading?: string;
	slug: string;
	fullslug: string;
	description: string | undefined;
	linkOnImage: boolean | undefined;
	tag: string | undefined;
	preview: boolean;
	slideshow?: boolean;
}) => {
	const Root = ({ children }: PropsWithChildren) => {
		if (linkOnImage === true)
			return (
				<div className="article-card__root">
					<div className={clsx(styles.root, "article-card")}>{children}</div>
				</div>
			);
		return (
			<Link
				href={fullslug.startsWith("/") ? fullslug : `/${fullslug}`}
				className="article-card__root"
			>
				<div className={clsx(styles.root, "article-card")}>{children}</div>
			</Link>
		);
	};
	const Heading = () =>
		!heading ? null : (
			<p className={clsx(styles.heading, "article-card__heading")}>
				<span>{heading}</span>
			</p>
		);
	const CardImage = () => {
		if (!imageBlok) return null;

		const Tag = () => {
			if (!tag) return null;
			return (
				<div className={clsx(styles.tag, "article-card__tag")}>
					<span>{tag}</span>
				</div>
			);
		};

		if (linkOnImage === true)
			return (
				<Link href={fullslug} className={clsx(styles.image, "article-card__image")}>
					<Tag />
					<StoryblokComponent blok={imageBlok} sizes={sizes} preview={preview} />
				</Link>
			);
		return (
			<div className={clsx(styles.image, "article-card__image")}>
				<Tag />
				<StoryblokComponent blok={imageBlok} sizes={sizes} preview={preview} />
			</div>
		);
	};
	const Description = () =>
		!description ? null : (
			<p className={clsx(styles.description, "article-card__description", "line-clamp-3")}>
				<span>{description}</span>
			</p>
		);

	return (
		<Root>
			<CardImage />
			<div className={clsx("article-card__content", slideshow && styles.slideshow)}>
				<Heading />
				<Description />
			</div>
		</Root>
	);
};

export const isArticleCard = (card: unknown): card is StoryblokStory<ArticleStoryblok> => {
	if (!card) return false;

	return (card as StoryblokStory<ArticleStoryblok>).content?.Title !== undefined;
};

export const Card = ({
	linkOnImage,
	article,
	preview,
	slideshow,
}: {
	linkOnImage?: boolean;
	article: BlokData["articleCard"] | StoryblokStory<ArticleStoryblok>;
	preview: boolean;
	slideshow?: boolean;
}) => {
	return !article ? null : (
		<ArticleCard
			imageBlok={article?.content?.listingImage?.[0]}
			sizes={getSizes(100, 25)}
			heading={article?.content?.Title}
			key={article?.uuid}
			slug={article?.slug}
			description={article?.content?.shortDescription}
			fullslug={article?.full_slug}
			linkOnImage={linkOnImage}
			tag={article?.content?.category}
			preview={preview}
			slideshow={slideshow}
		/>
	);
};

/**
 * @param linkOnImage - if set, only the image is a link instead of the whole card
 */
export const ArticleCardBlok = ({
	linkOnImage,
	blok,
	data,
	preview,
}: BlokProps<ArticleCardStoryblok, BlokData["articleCard"]> & {
	linkOnImage?: boolean;
}) => {
	if (!data) return null;

	return (
		<BlokWrap asChild editable={blok}>
			<Card linkOnImage={linkOnImage} article={data} preview={preview} />
		</BlokWrap>
	);
};
